import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  setDoc,
  Timestamp,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { isValidClientUuid } from "./deviceId";
import {
  getDeviceMerchantVisitInfo,
  getUserVisitDocRef,
  readVisitCountFromAggregate,
  recordCreditedDeviceVisit,
  MERCHANT_LINK_VISIT_COOLDOWN_MS,

} from "./visitsTracking";

/** 20-char alphanumeric loyalty id (matches Firestore schema doc). */
function generateLoyaltyId20() {
  const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < 20; i++) {
    out += chars[bytes[i] % chars.length];
  }
  return out;
}


/** Stable id for `users/{uid}/Visits/...` — Firebase Auth UID after phone verification. */
async function ensureFirebaseUserForWebVisits() {
  if (typeof window === "undefined") return null;
  return auth.currentUser?.uid ?? null;
}

/**
 * @param {Record<string, unknown>} r
 * @param {(v: unknown) => number | null} parsePositive
 */
function parseRewardRequiredVisits(r, parsePositive) {
  return (
      parsePositive(r?.cost_visits) ??
      parsePositive(r?.costVisits) ??
      parsePositive(r?.required_visits) ??
      parsePositive(r?.requiredVisits) ??
      parsePositive(r?.visits_required) ??
      parsePositive(r?.visitsRequired) ??
      null
  );
}

async function fetchRewardsForMerchant(db, merchantId) {
  const all = [];
  try {
    const subRef = collection(db, "merchants", merchantId, "rewards");
    const subSnap = await getDocs(subRef);
    if (!subSnap.empty) {
      subSnap.forEach((d) =>
          all.push({ id: d.id, merchant_id: merchantId, ...d.data() })
      );
      return all;
    }
  } catch (_) {
    /* try top-level */
  }
  for (const colName of ["Rewards", "rewards"]) {
    try {
      const ref = collection(db, colName);
      const qy = query(ref, where("merchant_id", "==", merchantId));
      const snap = await getDocs(qy);

      if (!snap.empty) {
        snap.forEach((d) => all.push({ id: d.id, ...d.data() }));
        return all;
      }
    } catch (_) {
      /* next */
    }
  }
  return all;
}

/** Tear down Phone Auth reCAPTCHA and empty the mount node so `render()` never hits "already been rendered". */
function clearKlerkRecaptchaMount() {
  if (typeof window === "undefined") return;
  try {
    const v = window.__klerkRecaptchaVerifier;
    if (v) {
      v.clear();
    }
  } catch (_) {
    /* ignore */
  }
  try {
    window.__klerkRecaptchaVerifier = null;
  } catch (_) {
    /* ignore */
  }
  const el = document.getElementById("recaptcha-container");
  if (el) {
    el.innerHTML = "";
  }
}

/**
 * NFC / merchant-link claim flow:
 * - Visit aggregates: `users/{firebaseAuthUid}/Visits/{merchantId}` (uid from Firebase Auth:
 *   phone-auth Firebase user
 * - No `klerk_device_id` cookie — identity is Firebase only.
 * - klerk_session cookie still ties browser return visits to `sessions.user_id` (same Firebase uid).
 * - OTP on first visit per merchant (with one retry next visit if skipped).
 */

export default function ClaimPointsPage() {
  const [tagId, setTagId] = useState("");

  const [visitsEarned] = useState(1);

  const [result, setResult] = useState(null);
  const [claimed, setClaimed] = useState(false);
  // UI timer should match the actual write cooldown enforced in `visitsTracking.js`.
  /** One-time tap token from the NFC redirect — consumed on visit record. */
  const [tapToken, setTapToken] = useState(null);
  const tapTokenRef = React.useRef(null);
  /** True when the page loaded without a valid tap token (e.g. plain refresh). */
  const [noTapToken, setNoTapToken] = useState(false);


  const [countryCode, setCountryCode] = useState("+1");
  const [lang, setLang] = useState("en");
  const isFr = lang === "fr";
  const [checkingSession, setCheckingSession] = useState(true);
  /** Bumped after OTP verify/skip so the visit effect re-runs and reads `klerk_resume_claim` from sessionStorage. */
  const [visitFlowNonce, setVisitFlowNonce] = useState(0);
  const [validTag, setValidTag] = useState(true);
  /** Set when opening `/?m={merchantId}` (browse reward without NFC tag). */
  const [merchantFromUrl, setMerchantFromUrl] = useState(null);

  const [merchantId, setMerchantId] = useState(null);
  const [merchantName, setMerchantName] = useState("this place");
  const [rewardThreshold, setRewardThreshold] = useState(5);
  /** Lowest required visits among merchant rewards (first tier); not overwritten by merchant-link finalize. */
  const [firstRewardRequired, setFirstRewardRequired] = useState(5);

  const [merchantReward, setMerchantReward] = useState(null); // { id, ...data }
  const [, setRewardLoadError] = useState("");

  const sessionToken = Cookies.get("klerk_session");

  const parsePositiveIntOrNull = useCallback((v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const i = Math.floor(n);
    return i > 0 ? i : null;
  }, []);

  // OTP gate (first visit per merchant; optional retry next visit if skipped)
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpStep, setOtpStep] = useState("phone"); // "phone" | "code"
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpDigits, setOtpDigits] = useState("");
  const [otpErrorKey, setOtpErrorKey] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otpPromptVisitNumber, setOtpPromptVisitNumber] = useState(null); // 1 | 2 | null
  const [pendingClaim, setPendingClaim] = useState(null); // { userId, isAnonymous, welcomeBack }
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  /** Remount `#recaptcha-container` after reset so Google’s widget never stacks on one DOM node. */
  const [recaptchaMountKey, setRecaptchaMountKey] = useState(0);

  const text = {
    invalidTitle: isFr ? "Lien invalide ⚠️" : "Invalid Link ⚠️",
    invalidSubtitle: isFr
        ? "Ce lien est manquant ou invalide."
        : "This tag link is missing or invalid.",
    loadingTitle: isFr ? "Enregistrement de votre visite…" : "Recording your visit…",
    loadingSubtitle: isFr ? "Veuillez patienter un instant ⏳" : "Please wait a moment ⏳",

    phoneTitle: isFr ? "Sauvegardez vos visites" : "Save your visits",
    phoneSubtitle: () =>
        isFr
            ? "Entrez votre numéro une seule fois. C’est tout."
            : "Enter your number once. That's it.",
    sendCode: isFr ? "Envoyer le code" : "Send code",
    sending: isFr ? "Envoi…" : "Sending…",
    preparing: isFr ? "Préparation…" : "Preparing verification…",
    finePrint: isFr
        ? "Utilisé seulement pour vous identifier. Jamais partagé."
        : "Only used to identify you. Never shared.",

    otpTitle: isFr ? "Entrez votre code" : "Enter your code",
    otpSubtitle: isFr ? `Envoyé au ${phoneNumber}` : `Sent to ${phoneNumber}`,
    confirm: isFr ? "Confirmer" : "Confirm",
    verifying: isFr ? "Vérification…" : "Verifying…",
    wrongNumber: isFr ? "Mauvais numéro?" : "Wrong number?",

    visitRecorded: isFr ? "Visite enregistrée" : "Visit recorded",
    visitsOf: (x, total) =>
        isFr ? `${x} sur ${total} visites` : `${x} of ${total} visits`,
    comeBack: (x) =>
        isFr
            ? `Revenez encore ${x} fois pour débloquer votre récompense.`
            : `Come back ${x} more times to unlock your reward.`,

    rewardEarnedTitle: isFr ? "Vous l’avez mérité." : "You earned it.",
    rewardEarnedSubtitle: (merchant) =>
        isFr
            ? `Téléchargez Klerk pour réclamer votre récompense chez ${merchant}.`
            : `Download Klerk to claim your reward at ${merchant}.`,
    getApp: isFr ? "Télécharger l’app" : "Get the app",

    rewardWaitingTitle: isFr ? "Visite enregistrée" : "Visit recorded",
    rewardWaitingSubtitle: (merchant) =>
        isFr
            ? `Vous avez gagné une récompense chez ${merchant}. Téléchargez Klerk avant qu’elle expire.`
            : `You've earned a reward at ${merchant}. Download Klerk before it expires.`,
    claimNow: isFr ? "Réclamer maintenant" : "Claim now",
    rewardWaitingVisitSubtitle: isFr
        ? "Vous avez une récompense qui vous attend — téléchargez Klerk pour la réclamer."
        : "You have a reward waiting — download Klerk to claim it.",
    noTapTitle: isFr
        ? "Touchez le tag NFC pour enregistrer votre visite"
        : "Tap the NFC tag to record your visit",

    noTapSubtitle: isFr
        ? "Actualiser la page n’enregistrera pas de visite — vous devez toucher le tag à chaque fois."
        : "Refreshing the page won't record a visit — you need to tap the tag each time.",
    errors: {
      sendOtp: isFr
          ? "Impossible d’envoyer le code. Vérifiez le numéro et réessayez."
          : "Could not send code. Check the number and try again.",

      enterPhone: isFr
          ? "Entrez votre numéro de téléphone."
          : "Enter your phone number.",

      invalidPhone: isFr
          ? "Utilisez le format international, ex. +15551234567."
          : "Use international format, e.g. +15551234567",

      verificationNotReady: isFr
          ? "La vérification n’est pas encore prête. Réessayez dans un instant."
          : "Verification isn't ready yet. Please wait a moment and try again.",

      incorrectCode: isFr
          ? "Code incorrect. Veuillez réessayer."
          : "Incorrect code. Please try again.",

      enterCode: isFr
          ? "Entrez le code à 6 chiffres."
          : "Enter the 6-digit code.",
    },
  };
  const getLocalStorageSafe = useCallback((key) => {
    if (typeof window === "undefined" || !window.localStorage) return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }, []);

  const setLocalStorageSafe = useCallback((key, val) => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(key, val);
    } catch {
      /* private mode or quota */
    }
  }, []);

  const phoneSavedKey = "klerk_phone_saved_v1";
  const isPhoneSavedLocally = useCallback(() => {
    return getLocalStorageSafe(phoneSavedKey) === "1";
  }, [getLocalStorageSafe]);

  const skipFirstOtpKey = useCallback(() => {
    if (!merchantId) return null;
    return `klerk_otp_skipped_first_${merchantId}`;
  }, [merchantId]);

  const wasSkippedFirstOtpPrompt = useCallback(() => {
    const k = skipFirstOtpKey();
    if (!k) return false;
    return getLocalStorageSafe(k) === "1";
  }, [getLocalStorageSafe, skipFirstOtpKey]);

  /**
   * `users/{userId}` after Phone Auth — document id is Firebase Auth uid (auto-generated), not device id.
   * Field names follow `firestore-schema.docx` § Users Collection.
   */
  const upsertUserProfileAfterPhoneAuth = useCallback(async (authUser) => {
    if (!authUser?.uid || !authUser.phoneNumber) return;

    const uid = authUser.uid;
    const phone = authUser.phoneNumber.trim();
    const userRef = doc(db, "users", uid);
    const snap = await getDoc(userRef);
    const nowTs = Timestamp.now();
    const isNew = !snap.exists();

    if (isNew) {
      await setDoc(userRef, {
        email: authUser.email || "",
        phone,
        phoneVerified: true,
        loyaltyId: generateLoyaltyId20(),
        acceptedTerms: false,
        acceptedTermsAt: null,
        termsVersion: "2025-04-26",
        isPartial: true,
        deletionRequested: false,
        deletionRequestedAt: null,
        deletionRequestedBy: null,
        createdAt: nowTs,
        lastUpdated: nowTs,
      });
      return;
    }

    await setDoc(
        userRef,
        {
          phone,
          phoneVerified: true,
          lastUpdated: nowTs,
        },
        { merge: true }
    );
  }, []);

  const bypassOtp = useCallback(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qp = (params.get("bypassOtp") || params.get("bypass_otp") || "").toLowerCase();
      if (qp !== "1" && qp !== "true" && qp !== "yes") return false;
      if (process.env.NODE_ENV !== "production") return true;
      // Staging / internal prod builds only — omit from real production .env
      return process.env.REACT_APP_BYPASS_OTP === "1";
    } catch (_) {
      return false;
    }
  }, []);

  const otpVerifiedKey = useCallback(() => {
    if (!merchantId) return null;
    return `klerk_otp_verified_${merchantId}`;
  }, [merchantId]);

  const isOtpVerifiedForMerchant = useCallback(() => {
    const key = otpVerifiedKey();
    if (!key) return false;
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const exp = parseInt(raw, 10);
    return Number.isFinite(exp) && exp > Date.now();
  }, [otpVerifiedKey]);

  const markOtpVerifiedForMerchant = useCallback(() => {
    const key = otpVerifiedKey();
    if (!key) return;
    // Once per merchant per browser session (30 minutes).
    sessionStorage.setItem(key, String(Date.now() + 30 * 60 * 1000));
  }, [otpVerifiedKey]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const m = (params.get("m") || "").trim();
    const t = params.get("t") || "";
    const token = params.get("token") || "";

    // Strip token from URL immediately — prevents re-use on refresh
    if (token && m) {
      window.history.replaceState({}, "", `/?m=${m}`);
      setTapToken(token);
      tapTokenRef.current = token;
    }

    if (m) {
      // Merchant-link flow: only proceed if a fresh tap token is present
      if (!token) {
        setNoTapToken(true);
        setCheckingSession(false);
        return;
      }
      setMerchantFromUrl(m);
      setTagId("");
      setValidTag(true);
      setCheckingSession(true);
      return;
    }
    setTagId(t);
    if (!t) {
      setValidTag(false);
      setCheckingSession(false);
    }
  }, []);

  useEffect(() => {
    // ✅ FIX: Wait until tagId has been read from the URL before running.
    // On the first render tagId is "" (initial state), not yet populated.
    // Without this guard, loadTag runs immediately with "" and sets validTag=false.
    if (tagId === "") {
      return;
    }

    const loadTag = async () => {
      try {
        const tagRef = doc(db, "tags", tagId);
        const tagSnap = await getDoc(tagRef);

        if (!tagSnap.exists()) {
          setValidTag(false);
          setCheckingSession(false);
          return;
        }

        const data = tagSnap.data();

        if (!data?.merchant_id) {
          setValidTag(false);
          setCheckingSession(false);
          return;
        }

        setMerchantId(data.merchant_id);
        setMerchantName(data.businessName || "this place");  // ← changed from merchant_name

        // Threshold fallback: tags.reward_threshold (merchant config can override below).
        const tagThreshold = parsePositiveIntOrNull(data.reward_threshold);
        const t = tagThreshold ?? 5;
        setRewardThreshold(t);
        setFirstRewardRequired(t);

      } catch (err) {
        console.error("Tag load error:", err);
        setValidTag(false);
        setCheckingSession(false);
      }
    };

    loadTag();
  }, [tagId, parsePositiveIntOrNull]);

  useEffect(() => {
    // Reward threshold source of truth (per your update): `rewards` collection.
    // We derive the *minimum* required visits (first reward tier).
    if (!merchantId) return;
    if (merchantFromUrl) return;

    let cancelled = false;
    (async () => {
      try {
        const candidates = await fetchRewardsForMerchant(db, merchantId);
        if (candidates.length === 0) return;

        let minRequired = null;
        candidates.forEach((r) => {
          const requiredVisits = parseRewardRequiredVisits(r, parsePositiveIntOrNull);
          if (requiredVisits == null) return;

          if (minRequired == null || requiredVisits < minRequired) {
            minRequired = requiredVisits;
          }
        });

        if (!cancelled && minRequired != null) {
          setRewardThreshold(minRequired);
          setFirstRewardRequired(minRequired);
        }
      } catch (e) {
        // Non-fatal: proceed with tag/default threshold
        console.warn("Rewards threshold load failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [merchantId, merchantFromUrl, parsePositiveIntOrNull]);

  useEffect(() => {
    // Fetch the reward to display for this merchant.
    // Rule: tags/{tagId}.merchant_id must match Rewards/{rewardId}.merchant_id.
    if (!merchantId) return;
    if (merchantFromUrl) return;
    let cancelled = false;
    setRewardLoadError("");
    setMerchantReward(null);

    (async () => {
      try {
        const all = await fetchRewardsForMerchant(db, merchantId);

        if (cancelled) return;
        if (all.length === 0) {
          setRewardLoadError("No reward configured for this merchant yet.");
          return;
        }

        // Prefer the "easiest" reward (lowest required visits) to match eligibility messaging.
        const withReq = all
            .map((r) => {
              const requiredVisits = parseRewardRequiredVisits(
                  r,
                  parsePositiveIntOrNull
              );
              return { ...r, __requiredVisits: requiredVisits };
            })
            .filter((r) => r.__requiredVisits != null);

        const chosen =
            [...withReq].sort((a, b) => a.__requiredVisits - b.__requiredVisits)[0] ??
            all[0];
        setMerchantReward(chosen);
        if (withReq.length > 0) {
          const sorted = [...withReq].sort(
              (a, b) => a.__requiredVisits - b.__requiredVisits
          );
          const firstR = sorted[0].__requiredVisits;
          setRewardThreshold(firstR);
          setFirstRewardRequired(firstR);
        }
      } catch (e) {
        if (cancelled) return;
        console.warn("Reward load failed:", e);
        setRewardLoadError("Could not load the reward right now.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [merchantId, merchantFromUrl, parsePositiveIntOrNull]);

  /** Shared UI after `recordCreditedDeviceVisit` for `/?m=` merchant deep links. */
  const finalizeMerchantLinkFlow = useCallback(
      ({ creditRes, all }) => {
        const userVisits = creditRes.visits;
        const withReq = all
            .map((r) => ({
              ...r,
              __requiredVisits: parseRewardRequiredVisits(
                  r,
                  parsePositiveIntOrNull
              ),
            }))
            .filter((r) => r.__requiredVisits != null);

        if (all.length === 0) {
          setRewardLoadError("No reward configured for this merchant yet.");
          setRewardThreshold(firstRewardRequired);
          setFirstRewardRequired(firstRewardRequired);
          setResult({
            success: creditRes.status !== "cooldown",
            merchantLink: true,
            visitCreditStatus: creditRes.status,
            cooldownRemainingMs: creditRes.cooldownRemainingMs,
            message:
                creditRes.status === "cooldown"
                    ? "Visit already counted. Wait for the timer, then refresh for another visit."
                    : "Check back soon for rewards at this place.",
            newVisits: userVisits,
            isEligible: false,
          });
          setClaimed(true);
          setCheckingSession(false);
          return;
        }

        if (withReq.length === 0) {
          setRewardLoadError("No reward configured for this merchant yet.");
          setRewardThreshold(firstRewardRequired);
          setFirstRewardRequired(firstRewardRequired);
          setResult({
            success: creditRes.status !== "cooldown",
            merchantLink: true,
            visitCreditStatus: creditRes.status,
            cooldownRemainingMs: creditRes.cooldownRemainingMs,
            message:
                creditRes.status === "cooldown"
                    ? "Visit already counted. Wait for the timer, then refresh for another visit."
                    : "Check back soon for rewards at this place.",
            newVisits: userVisits,
            isEligible: false,
          });
          setClaimed(true);
          setCheckingSession(false);
          return;
        }

        const eligible = withReq
            .filter((r) => r.__requiredVisits <= userVisits)
            .sort((a, b) => b.__requiredVisits - a.__requiredVisits);
        const bestAffordable = eligible[0] || null;

        const sortedAsc = [...withReq].sort(
            (a, b) => a.__requiredVisits - b.__requiredVisits
        );
        const displayReward = bestAffordable || sortedAsc[0];
        const firstR = sortedAsc[0].__requiredVisits;

        const passedFirst = userVisits > firstR;
        const reachedFirst = userVisits >= firstR;

        setMerchantReward(displayReward);
        setRewardThreshold(firstR);
        setFirstRewardRequired(firstR);


        const isEligible = bestAffordable != null;
        let message;
        if (creditRes.status === "success") {
          if (reachedFirst) {
            message = passedFirst ? "" : `Visit Count: ${userVisits}`;
          } else {
            message = `Visit added! ${userVisits} visit${
                userVisits === 1 ? "" : "s"
            } toward your next reward.`;
          }
        } else if (creditRes.status === "cooldown") {
          message = reachedFirst
              ? ""
              : "Visit already counted. Wait for the timer, then refresh for another visit.";
        } else if (creditRes.status === "cap_reached") {
          message = `Visit Count: ${userVisits}`;
        } else {
          if (reachedFirst) {
            message = `Visit Count: ${userVisits}`;
          } else {
            message = "Keep visiting to unlock your next reward.";
          }
        }

        setResult({
          success: creditRes.status !== "cooldown",
          merchantLink: true,
          visitCreditStatus: creditRes.status,
          cooldownRemainingMs: creditRes.cooldownRemainingMs,
          message,
          newVisits: userVisits,
          isEligible,
        });
        setClaimed(true);
        setCheckingSession(false);
      },
      [parsePositiveIntOrNull,firstRewardRequired]
  );

  const completeMerchantLinkAfterPhoneGate = useCallback(
      async (pending) => {
        const { mid, merchantNameForWrite, capThreshold } = pending;
        const visitUid = auth.currentUser?.uid;
        if (!visitUid) {
          finalizeMerchantLinkFlow({
            creditRes: { status: "error", visits: 0 },
            all: [],
          });
          return;
        }
        const MERCHANT_TAG = "merchant_link";
        let all = [];
        try {
          all = await fetchRewardsForMerchant(db, mid);
        } catch (e) {
          console.warn("Rewards load (merchant link resume) failed:", e);
        }

        let creditRes;
        try {
          creditRes = await recordCreditedDeviceVisit(db, {
            deviceId: visitUid,
            merchantId: mid,
            userId: undefined,
            tagId: MERCHANT_TAG,
            merchantName: merchantNameForWrite,
            rewardThreshold: capThreshold,
            visitsEarned: 1,
            cooldownMs: MERCHANT_LINK_VISIT_COOLDOWN_MS,
            tapToken: tapTokenRef.current,
          });
        } catch (e) {
          console.warn("Visit credit (merchant link) failed:", e);
          creditRes = { status: "cooldown", visits: 0, cooldownRemainingMs: 0 };
          try {
            const vRef = getUserVisitDocRef(db, visitUid, mid);
            const vSnap = await getDoc(vRef);
            const data = vSnap.exists() ? vSnap.data() : null;
            creditRes.visits = readVisitCountFromAggregate(data);
          } catch (_) {
            /* keep 0 */
          }
        }

        finalizeMerchantLinkFlow({ creditRes, all });
      },
      [finalizeMerchantLinkFlow]
  );

  useEffect(() => {
    if (!merchantFromUrl) return;
    let cancelled = false;
    setRewardLoadError("");
    setMerchantReward(null);

    (async () => {
      const mid = merchantFromUrl;
      const MERCHANT_TAG = "merchant_link";
      setMerchantId(mid);

      let merchantNameForWrite = "this place";
      try {
        const mSnap = await getDoc(doc(db, "merchants", mid));
        if (!cancelled && mSnap.exists()) {
          const d = mSnap.data();
          merchantNameForWrite =
              d.businessName || d.name || d.display_name || "this place";  // ← swap merchant_name for businessName
          setMerchantName(merchantNameForWrite);
        }
      } catch (_) {
        /* name is optional */
      }

      let all = [];
      try {
        all = await fetchRewardsForMerchant(db, mid);
      } catch (e) {
        console.warn("Rewards load (merchant link) failed:", e);
      }

      if (cancelled) return;

      const withReq = all
          .map((r) => ({
            ...r,
            __requiredVisits: parseRewardRequiredVisits(
                r,
                parsePositiveIntOrNull
            ),
          }))
          .filter((r) => r.__requiredVisits != null);

      const capThreshold =
          withReq.length > 0
              ? Math.min(...withReq.map((r) => r.__requiredVisits))
              : 5;

      setRewardThreshold(capThreshold);
      setFirstRewardRequired(capThreshold);

      const visitUid = await ensureFirebaseUserForWebVisits();
      if (!visitUid) {
        clearKlerkRecaptchaMount();
        setRecaptchaReady(false);
        setRecaptchaMountKey((k) => k + 1);

        setPendingClaim({
          kind: "merchant_link",
          mid,
          merchantNameForWrite,
          capThreshold,
        });

        setOtpPromptVisitNumber(1);
        setOtpRequired(true);
        setCheckingSession(false);
        return;
      }

      const visitInfo = await getDeviceMerchantVisitInfo(db, visitUid, mid);
      if (cancelled) return;

      const nextVisitNumber = (visitInfo?.visits ?? 0) + 1;
      const shouldPromptOn1 = nextVisitNumber === 1 && !isPhoneSavedLocally();
      const shouldPromptRetry =
          nextVisitNumber === 2 && !isPhoneSavedLocally() && wasSkippedFirstOtpPrompt();
      const shouldPrompt = (shouldPromptOn1 || shouldPromptRetry) && !bypassOtp();

      if (shouldPrompt && !isOtpVerifiedForMerchant()) {
        clearKlerkRecaptchaMount();
        setRecaptchaReady(false);
        setRecaptchaMountKey((k) => k + 1);
        setPendingClaim({
          kind: "merchant_link",
          mid,
          merchantNameForWrite,
          capThreshold,
        });
        setOtpPromptVisitNumber(nextVisitNumber);
        setOtpRequired(true);
        setCheckingSession(false);
        return;
      }

      let creditRes;
      try {
        creditRes = await recordCreditedDeviceVisit(db, {
          deviceId: visitUid,
          merchantId: mid,
          userId: undefined,
          tagId: MERCHANT_TAG,
          merchantName: merchantNameForWrite,
          rewardThreshold: capThreshold,
          visitsEarned: 1,
          cooldownMs: MERCHANT_LINK_VISIT_COOLDOWN_MS,
          tapToken: tapTokenRef.current,
        });
      } catch (e) {
        console.warn("Visit credit (merchant link) failed:", e);
        creditRes = { status: "cooldown", visits: 0, cooldownRemainingMs: 0 };
        try {
          const vRef = getUserVisitDocRef(db, visitUid, mid);
          const vSnap = await getDoc(vRef);
          const data = vSnap.exists() ? vSnap.data() : null;
          creditRes.visits = readVisitCountFromAggregate(data);
        } catch (_) {
          /* keep 0 */
        }
      }

      if (cancelled) return;

      finalizeMerchantLinkFlow({ creditRes, all });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    merchantFromUrl,
    parsePositiveIntOrNull,
    isPhoneSavedLocally,
    wasSkippedFirstOtpPrompt,
    bypassOtp,
    isOtpVerifiedForMerchant,
    finalizeMerchantLinkFlow,
  ]);

  const logTapEvent = useCallback(
      async (userId) => {
        const tapUid = auth.currentUser?.uid ?? userId;
        try {
          const nowTs = Timestamp.now();
          await addDoc(collection(db, "tap_events"), {
            tag_id: tagId,
            merchant_id: merchantId,
            user_id: userId,
            device_id: tapUid,
            is_anonymous: false,
            timestamp_server: nowTs,
            credited: true,
            credited_type: "visit",
            credited_amount: visitsEarned,
          });
        } catch (err) {
          console.error("Tap event log failed:", err);
        }
      },
      [tagId, merchantId, visitsEarned]
  );

  const recordVisitForDevice = useCallback(
      async (deviceId, userId) => {
        if (!merchantId) throw new Error("Missing merchantId");
        if (!deviceId) throw new Error("Missing deviceId");
        return recordCreditedDeviceVisit(db, {
          deviceId,
          merchantId,
          userId:
              userId && userId !== deviceId ? userId : undefined,
          tagId,
          rewardThreshold,
          visitsEarned,
        });
      },
      [merchantId, tagId, rewardThreshold, visitsEarned]
  );

  useEffect(() => {
    if (!otpRequired) return;
    if (recaptchaReady) return;
    if (typeof window === "undefined") return;

    let cancelled = false;

    const ensureRecaptcha = async () => {
      try {
        clearKlerkRecaptchaMount();
        if (cancelled) return;

        await new Promise((r) => requestAnimationFrame(() => r(undefined)));

        window.__klerkRecaptchaVerifier = new RecaptchaVerifier(
            auth,
            "recaptcha-container",
            { size: "invisible" }
        );
        await window.__klerkRecaptchaVerifier.render();
        if (!cancelled) {
          setRecaptchaReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Recaptcha init failed:", e);
          setOtpErrorKey("Could not start verification. Please refresh and try again.");
        }
      }
    };

    void ensureRecaptcha();

    return () => {
      cancelled = true;
      // Do not clear the mount here: when `setRecaptchaReady(true)` runs, this cleanup would
      // fire and destroy the verifier while `recaptchaReady` stays true — "Send code" then fails.
    };
  }, [otpRequired, recaptchaReady, recaptchaMountKey]);

  useEffect(() => {
    if (otpRequired) return;
    clearKlerkRecaptchaMount();
    setRecaptchaReady(false);
  }, [otpRequired]);

  /** Phone Auth consumes the verifier; always build a new one for the next SMS / OTP session. */
  const resetKlerkRecaptcha = useCallback(() => {
    clearKlerkRecaptchaMount();
    setRecaptchaReady(false);
    setRecaptchaMountKey((k) => k + 1);
  }, []);

  const sendOtp = useCallback(async (phoneOverride = null) => {
    setOtpErrorKey("");

    const pn =
        typeof phoneOverride === "string"
            ? phoneOverride.trim()
            : (phoneNumber || "").trim();
    if (!pn) {
      setOtpErrorKey("enterPhone");
      return;
    }

    if (!pn.startsWith("+")) {
      setOtpErrorKey("invalidPhone");
      return;
    }

    if (!recaptchaReady || !window.__klerkRecaptchaVerifier) {
      setOtpErrorKey("verificationNotReady");
      return;
    }

    setOtpLoading(true);

    try {
      const verifier = window.__klerkRecaptchaVerifier;

      const cr = await signInWithPhoneNumber(auth, pn, verifier);

      setConfirmationResult(cr);
      setOtpStep("code");
      setOtpDigits("");

      // Do NOT reset reCAPTCHA here.
    } catch (e) {
      console.error("sendOtp failed:", e);
      setOtpErrorKey("sendOtp");

      setTimeout(() => {
        resetKlerkRecaptcha();
      }, 300);
    } finally {
      setOtpLoading(false);
    }
  }, [phoneNumber, recaptchaReady, resetKlerkRecaptcha]);

  const verifyOtpAndContinue = useCallback(async (codeOverride = null) => {
    setOtpErrorKey("");

    const code =
        typeof codeOverride === "string"
            ? codeOverride
            : otpDigits;


    if (!confirmationResult) {
      setOtpErrorKey("Please request a code first.");
      return;
    }
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setOtpErrorKey("enterCode");
      return;
    }

    setOtpLoading(true);
    try {
      console.log("OTP code entered:", code);
      console.log("Before confirm UID:", auth.currentUser?.uid);
      console.log("Before confirm phone:", auth.currentUser?.phoneNumber);
      console.log("confirmationResult:", confirmationResult);

      const result = await confirmationResult.confirm(code);

      console.log("After confirm UID:", result.user.uid);
      console.log("After confirm phone:", result.user.phoneNumber);
      console.log("Current auth UID:", auth.currentUser?.uid);
      console.log("Current auth phone:", auth.currentUser?.phoneNumber);
      try {
        await upsertUserProfileAfterPhoneAuth(auth.currentUser);
        setLocalStorageSafe(phoneSavedKey, "1");
      } catch (e) {
        console.warn("user profile save failed:", e);
      }
      markOtpVerifiedForMerchant();
      resetKlerkRecaptcha();
      setOtpRequired(false);
      setOtpStep("phone");
      setConfirmationResult(null);
      setOtpPromptVisitNumber(null);

      const p = pendingClaim;
      setPendingClaim(null);
      if (p?.kind === "merchant_link") {
        setCheckingSession(true);
        try {
          await completeMerchantLinkAfterPhoneGate(p);
        } catch (err) {
          console.error("Merchant link resume after OTP failed:", err);
          setResult({
            success: false,
            merchantLink: true,
            visitCreditStatus: "error",
            message: "Could not record your visit. Please refresh and try again.",
            newVisits: 0,
            isEligible: false,
          });
          setClaimed(true);
        } finally {
          setCheckingSession(false);
        }
      } else if (p?.userId) {
        setCheckingSession(true);
        sessionStorage.setItem("klerk_resume_claim", JSON.stringify(p));
        setVisitFlowNonce((n) => n + 1);
      }
    } catch (e) {
      console.error("verifyOtp failed:", e);
      setOtpErrorKey("incorrectCode");
    } finally {
      setOtpLoading(false);
    }
  }, [

    completeMerchantLinkAfterPhoneGate,
    confirmationResult,
    markOtpVerifiedForMerchant,
    otpDigits,
    pendingClaim,
    resetKlerkRecaptcha,
    setLocalStorageSafe,
    upsertUserProfileAfterPhoneAuth
  ]);



  useEffect(() => {
    if (claimed) return;
    if (!tagId || !merchantId) return;

    const applyVisitResult = async (
        userId,
        { welcomeBack },
        resumeAfterPhoneGate = false
    ) => {
      const visitUid = userId;
      if (!visitUid) {
        setResult({
          success: false,
          visitError: true,
          message: "Could not record your visit. Please try again.",
          newVisits: 0,
          isEligible: false,
        });
        setClaimed(true);
        return true;
      }

      // Replay last success on refresh only — skip when resuming after OTP/skip (same tag still in sessionStorage).
      if (!resumeAfterPhoneGate) {
        const lastTag = sessionStorage.getItem("claimed_tag");
        if (lastTag === tagId) {
          const agRef = getUserVisitDocRef(db, visitUid, merchantId);
          const visitSnap = await getDoc(agRef);
          const data = visitSnap.exists() ? visitSnap.data() : null;
          const currentVisits = data ? readVisitCountFromAggregate(data) : 0;
          const firstR = firstRewardRequired;
          const isEligible = currentVisits >= firstR;

          setResult({
            success: true,
            message:
                currentVisits >= firstR
                    ? `${welcomeBack ? "Welcome back! " : ""}Visit Count: ${currentVisits}`
                    : `${welcomeBack ? "Welcome back! " : ""}Visit added ✅ ${currentVisits}/${firstR} visits at ${merchantName}`,
            newVisits: currentVisits,
            isEligible,
          });
          setClaimed(true);
          return true;
        }
      }

      // OTP on first visit per merchant; one retry on the next visit if they skipped.
      const visitInfo = await getDeviceMerchantVisitInfo(db, visitUid, merchantId);
      // - visit #1: verify phone before the first credited visit
      // - visit #2: prompt again only if they skipped on #1
      const nextVisitNumber = (visitInfo?.visits ?? 0) + 1;
      const shouldPromptOn1 = nextVisitNumber === 1 && !isPhoneSavedLocally();
      const shouldPromptRetry =
          nextVisitNumber === 2 && !isPhoneSavedLocally() && wasSkippedFirstOtpPrompt();
      const shouldPrompt = (shouldPromptOn1 || shouldPromptRetry) && !bypassOtp();

      if (shouldPrompt && !isOtpVerifiedForMerchant()) {
        resetKlerkRecaptcha();
        setPendingClaim({ userId, welcomeBack });
        setOtpPromptVisitNumber(nextVisitNumber);
        setOtpRequired(true);
        setCheckingSession(false);
        return true;
      }

      const creditRes = await recordVisitForDevice(visitUid, userId);
      sessionStorage.setItem("claimed_tag", tagId);
      sessionStorage.setItem("claim_expire_at", (Date.now() + 60000).toString());

      if (creditRes.status === "cooldown") {
        setResult({
          success: false,
          message: "You recently claimed a visit here. Please wait 5 minutes between claims.",
          newVisits: creditRes.visits,
          isEligible: creditRes.visits >= firstRewardRequired,
        });
        setClaimed(true);
        return true;
      }
      if (creditRes.status === "cap_reached") {
        setResult({
          success: true,
          isEligible: creditRes.visits >= firstRewardRequired,
          message: `${welcomeBack ? "Welcome back! " : ""}Visit Count: ${creditRes.visits}`,
          newVisits: creditRes.visits,
        });
        setClaimed(true);
        return true;
      }

      const newVisits = creditRes.visits;
      await logTapEvent(userId);

      const firstR = firstRewardRequired;
      const isEligible = newVisits >= firstR;

      setResult({
        success: true,
        message:
            newVisits >= firstR
                ? `${welcomeBack ? "Welcome back! " : ""}Visit Count: ${newVisits}`
                : `${welcomeBack ? "Welcome back! " : ""}Visit added ✅ ${newVisits}/${firstR} visits at ${merchantName}`,
        newVisits,
        isEligible,
      });

      setClaimed(true);
      return true;
    };

    const ensureSessionForUser = async (firebaseUid) => {
      const token = Cookies.get("klerk_session");
      if (token) return;

      const newToken = uuidv4();
      const nowTs = Timestamp.now();
      Cookies.set("klerk_session", newToken, { expires: 30 });
      await addDoc(collection(db, "sessions"), {
        session_token: newToken,
        user_id: firebaseUid,
        device_id: firebaseUid,
        is_anonymous: false,
        created_at: nowTs,
        expires_at: Date.now() + 30 * 24 * 60 * 60 * 1000,
      });
    };

    const recordVisit = async () => {
      const token = Cookies.get("klerk_session");

      try {
        const firebaseUid = await ensureFirebaseUserForWebVisits();
        if (!firebaseUid) {
          resetKlerkRecaptcha();
          setPendingClaim({
            userId: null,
            welcomeBack: false,
          });
          setOtpPromptVisitNumber(1);
          setOtpRequired(true);
          setCheckingSession(false);
          return;
        }

        // If an OTP verification just completed, resume the pending claim quickly.
        const resumeRaw = sessionStorage.getItem("klerk_resume_claim");
        if (resumeRaw) {
          sessionStorage.removeItem("klerk_resume_claim");
          try {
            const p = JSON.parse(resumeRaw);
            if (p?.userId) {
              const done = await applyVisitResult(
                  firebaseUid,
                  {
                    isAnonymous: false,
                    welcomeBack: !!p.welcomeBack,
                  },
                  true
              );
              if (done) return;
            }
          } catch (_) {}
        }

        if (token) {
          const sessionsRef = collection(db, "sessions");
          const q = query(sessionsRef, where("session_token", "==", token));
          const snap = await getDocs(q);

          if (!snap.empty) {
            const sessionData = snap.docs[0].data();
            const sid = sessionData.user_id;
            if (!sid || isValidClientUuid(sid) || sid !== firebaseUid) {
              Cookies.remove("klerk_session");
              await ensureSessionForUser(firebaseUid);
              const done = await applyVisitResult(firebaseUid, {

                welcomeBack: false,
              });
              if (done) return;
            } else {
              const done = await applyVisitResult(firebaseUid, {

                welcomeBack: true,
              });
              if (done) return;
            }
          } else {
            Cookies.remove("klerk_session");
            await ensureSessionForUser(firebaseUid);
            const done = await applyVisitResult(firebaseUid, {

              welcomeBack: false,
            });
            if (done) return;
          }
        } else {
          await ensureSessionForUser(firebaseUid);
          const done = await applyVisitResult(firebaseUid, {

            welcomeBack: false,
          });
          if (done) return;
        }
      } catch (err) {
        console.error("Visit recording error:", err);
        setResult({
          success: false,
          visitError: true,
          message: "Could not record your visit. Please try again.",
          newVisits: 0,
        });
        setClaimed(true);
      } finally {
        setCheckingSession(false);
      }
    };

    recordVisit();
  }, [
    sessionToken,
    tagId,
    merchantId,
    visitsEarned,
    rewardThreshold,
    firstRewardRequired,
    merchantName,
    claimed,
    recordVisitForDevice,
    logTapEvent,
    isOtpVerifiedForMerchant,
    bypassOtp,
    isPhoneSavedLocally,
    wasSkippedFirstOtpPrompt,
    resetKlerkRecaptcha,
    visitFlowNonce,
  ]);



  const currentVisitsForUI = result?.newVisits ?? 0;

  const styles = {
    page: {
      backgroundColor: "#ffffff",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
      padding: "24px",
    },
    phoneFrame: {
      width: "100%",
      maxWidth: 420,
      borderRadius: 28,
      padding: 18,
      boxShadow: "0 18px 50px rgba(30, 41, 59, 0.12)",
      border: "1px solid rgba(148,163,184,0.25)",
      background:
          "linear-gradient(180deg, rgba(250,250,255,0.9), rgba(255,255,255,1))",
    },
    screen: {
      borderRadius: 22,
      padding: "28px 22px",
      border: "1px solid rgba(148,163,184,0.22)",
      backgroundColor: "#fff",
    },
    brand: {
      fontSize: 26,
      fontWeight: 800,
      color: "#6D28D9",
      textAlign: "center",
      marginBottom: 26,
      letterSpacing: 0.2,
    },
    h1: {
      fontSize: 22,
      fontWeight: 800,
      color: "#0f172a",
      textAlign: "center",
      margin: 0,
    },
    merchant: {
      marginTop: 10,
      fontSize: 18,
      fontWeight: 700,
      color: "#111827",
      textAlign: "center",
    },
    sub: {
      marginTop: 6,
      fontSize: 14,
      color: "#64748b",
      textAlign: "center",
      lineHeight: 1.35,
    },
    phoneInputWrap: {
      marginTop: 22,
      borderRadius: 12,
      border: "1.5px solid rgba(148,163,184,0.35)",
      display: "flex",
      alignItems: "center",
      backgroundColor: "#fff",
      overflow: "hidden",
    },

    countrySelect: {
      width: 115,
      height: 58,
      border: "none",
      borderRight: "1.5px solid rgba(148,163,184,0.45)",
      outline: "none",
      paddingLeft: 14,
      fontSize: 16,
      fontWeight: 800,
      color: "#0f172a",
      backgroundColor: "#fff",
      cursor: "pointer",
    },

    phoneOnlyInput: {
      flex: 1,
      height: 58,
      border: "none",
      outline: "none",
      fontSize: 17,
      color: "#0f172a",
      background: "transparent",
      padding: "0 16px",
    },
    inputPrefix: {
      fontSize: 14,
      fontWeight: 700,
      color: "#64748b",
    },
    input: {
      flex: 1,
      border: "none",
      outline: "none",
      fontSize: 16,
      color: "#0f172a",
      background: "transparent",
    },
    primaryBtn: {
      width: "100%",
      marginTop: 18,
      padding: "14px 16px",
      borderRadius: 12,
      border: "none",
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 16,
      color: "#fff",
      background: "linear-gradient(180deg, #6D28D9, #5B21B6)",
      boxShadow: "0 10px 18px rgba(109,40,217,0.25)",
    },
    primaryBtnDisabled: {
      opacity: 0.6,
      cursor: "not-allowed",
    },
    helper: {
      marginTop: 14,
      fontSize: 13,
      color: "#94a3b8",
      textAlign: "center",
    },
    resultLine: {
      marginTop: 14,
      fontSize: 14,
      fontWeight: 700,
      textAlign: "center",
    },
    otpRow: {
      marginTop: 18,
      display: "flex",
      justifyContent: "center",
      gap: 10,
    },
    otpBoxInput: {
      width: 44,
      height: 48,
      borderRadius: 12,
      border: "1.5px solid rgba(148,163,184,0.45)",
      textAlign: "center",
      fontSize: 20,
      fontWeight: 800,
      color: "#0f172a",
      backgroundColor: "#F8FAFC",
      outline: "none",
    },
    otpSingleInput: {
      width: "100%",
      height: 68,
      marginTop: 22,
      borderRadius: 16,
      border: "1.5px solid rgba(148,163,184,0.45)",
      textAlign: "center",
      fontSize: 34,
      fontWeight: 900,
      color: "#0f172a",
      backgroundColor: "#F8FAFC",
      outline: "none",
      letterSpacing: "18px",
      paddingLeft: 18,
      boxSizing: "border-box",
    },
    otpHintDots: {
      marginTop: 10,
      display: "flex",
      justifyContent: "center",
      gap: 6,
      opacity: 0.6,
    },
    dot: (active) => ({
      width: active ? 18 : 6,
      height: 6,
      borderRadius: 999,
      backgroundColor: active ? "#6D28D9" : "#CBD5E1",
      transition: "all 150ms ease",
    }),
    successIconWrap: {
      marginTop: 8,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 12,
    },
    checkCircle: {
      width: 34,
      height: 34,
      borderRadius: 999,
      backgroundColor: "#16A34A",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      fontWeight: 900,
      fontSize: 18,
    },
    successTitle: {
      fontSize: 22,
      fontWeight: 900,
      color: "#0f172a",
      margin: 0,
    },
    starsRow: {
      marginTop: 18,
      display: "flex",
      justifyContent: "center",
      gap: 10,
    },
    starFilled: { fontSize: 22 },
    starEmpty: { fontSize: 22, opacity: 0.25 },
    divider: {
      marginTop: 18,
      marginBottom: 14,
      height: 1,
      backgroundColor: "rgba(148,163,184,0.25)",
      border: 0,
    },
    bigLine: {
      textAlign: "center",
      fontSize: 16,
      fontWeight: 800,
      color: "#0f172a",
      marginTop: 10,
    },
    smallLine: {
      textAlign: "center",
      fontSize: 14,
      color: "#64748b",
      marginTop: 6,
      fontWeight: 600,
    },
    downloadCta: {
      marginTop: 18,
      textAlign: "center",
      color: "#64748b",
      fontSize: 14,
      fontWeight: 600,
    },
    storeBadges: {
      marginTop: 14,
      display: "flex",
      justifyContent: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    badge: {
      height: 40,
      width: "auto",
      maxWidth: 180,
    },
    rewardCard: {
      marginTop: 16,
      borderRadius: 14,
      border: "1px solid rgba(148,163,184,0.28)",
      backgroundColor: "#F8FAFC",
      padding: 14,
    },
    rewardLabel: {
      fontSize: 12,
      fontWeight: 900,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      color: "#6D28D9",
    },
    rewardName: {
      marginTop: 6,
      fontSize: 16,
      fontWeight: 900,
      color: "#0f172a",
    },
    rewardDesc: {
      marginTop: 6,
      fontSize: 13,
      fontWeight: 650,
      color: "#64748b",
      lineHeight: 1.35,
    },
    langToggle: {
      position: "fixed",
      top: 22,
      right: 24,
      zIndex: 999,
      border: "none",
      borderRadius: 999,
      padding: "14px 22px",
      fontSize: 18,
      fontWeight: 900,
      color: "#fff",
      background: "linear-gradient(180deg, #6D28D9, #5B21B6)",
      boxShadow: "0 10px 22px rgba(109,40,217,0.25)",
      cursor: "pointer",
    },

    appButton: {
      width: "100%",
      marginTop: 18,
      padding: "14px 16px",
      borderRadius: 12,
      border: "none",
      cursor: "pointer",
      fontWeight: 900,
      fontSize: 16,
      color: "#fff",
      background: "linear-gradient(180deg, #6D28D9, #5B21B6)",
      boxShadow: "0 10px 18px rgba(109,40,217,0.25)",
      textAlign: "center",
      boxSizing: "border-box",
    },

    secondaryBtn: {
      width: "100%",
      marginTop: 12,
      padding: "12px 14px",
      borderRadius: 12,
      border: "1px solid rgba(109,40,217,0.35)",
      cursor: "pointer",
      fontWeight: 900,
      fontSize: 15,
      color: "#5B21B6",
      background: "#ffffff",
    },
  };




  const TranslateToggle = () => (
      <button
          style={styles.langToggle}
          onClick={() => setLang(isFr ? "en" : "fr")}
      >
        {isFr ? "EN" : "FR"}
      </button>
  );

  const renderPageShell = (children) => (
      <div style={styles.page}>
        <TranslateToggle />

        <div style={styles.phoneFrame}>
          <div style={styles.screen}>{children}</div>
        </div>
      </div>
  );

  if (!validTag) {
    return renderPageShell(
        <>
          <div style={styles.brand}>Klerk</div>
          <h1 style={styles.h1}>{text.invalidTitle}</h1>
          <div style={styles.sub}>{text.invalidSubtitle}</div>
        </>
    );
  }

  if (checkingSession) {
    return renderPageShell(
        <>
          <div style={styles.brand}>Klerk</div>
          <h1 style={styles.h1}>{text.loadingTitle}</h1>
          <div style={styles.sub}>{text.loadingSubtitle}</div>
        </>
    );
  }

  if (otpRequired) {
    const codeFilled = /^\d{6}$/.test(otpDigits);
    return renderPageShell(
        <>
          <div style={styles.brand}>Klerk</div>
          <h1 style={styles.h1}>{otpStep === "phone" ? text.phoneTitle : text.otpTitle}</h1>
          <div style={styles.sub}>
            {otpStep === "phone" ? text.phoneSubtitle(otpPromptVisitNumber) : text.otpSubtitle}
          </div>

          <div key={recaptchaMountKey} id="recaptcha-container" />

          {otpStep === "phone" ? (
              <>
                <div style={styles.phoneInputWrap}>
                  <select
                      style={styles.countrySelect}
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+1">CA +1</option>
                  </select>

                  <input
                      style={styles.phoneOnlyInput}
                      value={phoneNumber.replace(countryCode, "")}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, "");

                        let fullPhone = "";

                        // Autofill sometimes inserts 11 digits: 14383084672
                        if (raw.length === 11 && raw.startsWith("1")) {
                          setCountryCode("+1");

                          fullPhone = "+1" + raw.slice(1);

                          setPhoneNumber(fullPhone);

                          if (
                              recaptchaReady &&
                              window.__klerkRecaptchaVerifier &&
                              otpStep === "phone"
                          ) {
                            setTimeout(() => {
                              sendOtp(fullPhone);
                            }, 300);
                          }

                          return;
                        }

                        // Autofill sometimes inserts only local number: 4383084672
                        if (raw.length === 10) {
                          fullPhone = "+1" + raw;

                          setPhoneNumber(fullPhone);

                          if (
                              recaptchaReady &&
                              window.__klerkRecaptchaVerifier &&
                              otpStep === "phone"
                          ) {
                            setTimeout(() => {
                              sendOtp(fullPhone);
                            }, 300);
                          }

                          return;
                        }

                        // User typing normally
                        fullPhone = "+1" + raw.slice(0, 10);

                        setPhoneNumber(fullPhone);
                      }}
                      placeholder="_ _ _ _ _ _ _ _ _ _"
                      inputMode="tel"
                      type="tel"
                      autoComplete="tel"
                      name="phone"
                  />
                </div>
                <button
                    style={{
                      ...styles.primaryBtn,
                      ...(otpLoading || !recaptchaReady
                          ? styles.primaryBtnDisabled
                          : null),
                    }}
                    disabled={otpLoading || !recaptchaReady}
                    onClick={() => sendOtp()}
                >
                  {otpLoading
                      ? text.sending
                      : !recaptchaReady
                          ? text.preparing
                          : text.sendCode}
                </button>

                <div style={styles.helper}>{text.finePrint}</div>
              </>
          ) : (
              <>
                <input
                    style={styles.otpSingleInput}
                    value={otpDigits}
                    onChange={(e) => {
                      const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6);

                      setOtpDigits(value);

                      if (value.length === 6 && !otpLoading && confirmationResult) {
                        setTimeout(() => {
                          verifyOtpAndContinue(value);
                        }, 100);
                      }
                    }}
                    inputMode="numeric"
                    type="text"
                    maxLength={6}
                    placeholder="_ _ _ _ _ _"
                    autoComplete="one-time-code"
                    name="one-time-code"
                />
                <button
                    style={{
                      ...styles.primaryBtn,
                      ...((!codeFilled || otpLoading) ? styles.primaryBtnDisabled : null),
                    }}
                    disabled={!codeFilled || otpLoading}
                    onClick={() => verifyOtpAndContinue}
                >
                  {otpLoading ? text.verifying : text.confirm}
                </button>

                <div style={styles.helper}>

                  <button
                      onClick={() => {
                        setOtpStep("phone");
                        setConfirmationResult(null);
                        setOtpDigits("");
                        setOtpErrorKey("");
                      }}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#6D28D9",
                        fontWeight: 800,
                        cursor: "pointer",
                        padding: 0,
                      }}
                  >
                    {text.wrongNumber}
                  </button>
                </div>
              </>
          )}

          {otpErrorKey ? (
              <div style={{ ...styles.resultLine, color: "#DC2626" }}>
                {text.errors[otpErrorKey]}
              </div>
          ) : null}
        </>
    );
  }

  const renderSuccessScreen = () => {
    const isVisitError = result && result.visitError;
    const isCooldown = result && result.success === false && !isVisitError;
    const visits = currentVisitsForUI;
    const firstR = firstRewardRequired;
    const passedFirst = visits > firstR;
    const reachedFirst = visits >= firstR && firstR > 0;
    const progressFilled = Math.min(visits, firstR);
    const remainingToFirst = Math.max(0, firstR - visits);
    const displayMerchantName =
        merchantReward?.merchantName ||
        merchantReward?.merchant_name ||
        merchantName ||
        "this store";
    const merchantCooldownAtReward =
        !!result?.merchantLink &&
        isCooldown &&
        reachedFirst &&
        result.visitCreditStatus === "cooldown";

    let iconBg = "#16A34A";
    let iconChar = "✓";
    let titleStr = reachedFirst && !passedFirst ? text.rewardEarnedTitle : text.visitRecorded;

    if (isVisitError) {
      iconBg = "#DC2626";
      iconChar = "!";
      titleStr = isFr ? "Une erreur est survenue" : "Something went wrong";
    } else if (isCooldown) {
      iconBg = "#F59E0B";
      iconChar = "⏳";
      titleStr = isFr ? "Trop tôt" : "Too Soon";
    } else if (passedFirst) {
      iconBg = "#16A34A";
      iconChar = "✓";
      titleStr = text.rewardWaitingTitle;
    } else if (reachedFirst) {
      iconBg = "#16A34A";
      iconChar = "✓";
      titleStr = text.rewardEarnedTitle;
    } else if (result?.merchantLink) {
      iconBg = "#6D28D9";
      iconChar = "⭐";
      titleStr = isFr ? "Vos récompenses" : "Your rewards";
    }

    return (
        <>
          <div style={styles.brand}>Klerk</div>

          <div
              style={
                result?.merchantLink
                    ? {
                      marginTop: 8,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      width: "100%",
                    }
                    : styles.successIconWrap
              }
          >
            <div
                style={{
                  ...styles.checkCircle,
                  backgroundColor: iconBg,
                }}
            >
              {iconChar}
            </div>
            <h1
                style={{
                  ...styles.successTitle,
                  ...(result?.merchantLink ? { textAlign: "center", marginTop: 10 } : {}),
                }}
            >
              {titleStr}
            </h1>
            {result?.merchantLink && !isVisitError ? (
                merchantCooldownAtReward ? (
                    <div
                        style={{
                          ...styles.sub,
                          marginTop: 10,
                          maxWidth: 360,
                          fontWeight: 600,
                        }}
                    >
                      {text.rewardWaitingSubtitle(displayMerchantName)}
                    </div>
                ) : result?.message ? (
                    <div
                        style={{
                          ...styles.sub,
                          marginTop: 8,
                          maxWidth: 360,
                          marginLeft: "auto",
                          marginRight: "auto",
                        }}
                    >
                      {isCooldown
                          ? (isFr
                              ? "Visite déjà comptée."
                              : "Visit already counted.")
                          : !reachedFirst
                              ? text.visitRecorded
                              : ( passedFirst ? text.rewardWaitingVisitSubtitle : null)}
                    </div>
                ) : null
            ) : null}
          </div>

          {!isVisitError ? (
              <>
                {!passedFirst ? (
                    <>
                      <div style={styles.starsRow}>
                        {Array.from({ length: firstR }, (_, i) => {
                          const filled = i < progressFilled;
                          return (
                              <span key={i} style={filled ? styles.starFilled : styles.starEmpty}>
                      ⭐
                    </span>
                          );
                        })}
                      </div>

                      <div style={styles.smallLine}>
                        {text.visitsOf(progressFilled, firstR)}
                      </div>
                    </>
                ) : null}

                <hr style={styles.divider} />

                {!passedFirst ? (
                    <div style={styles.bigLine}>
                      {!reachedFirst
                          ? text.comeBack(remainingToFirst)
                          : null}
                    </div>
                ) : null}

                {reachedFirst ? (
                    <>
                      {!passedFirst ? (
                          <div style={styles.rewardCard}>
                            <div style={styles.rewardLabel}>
                              {isFr ? "Votre récompense" : "Your reward"}
                            </div>
                            <div style={styles.rewardName}>
                              {merchantReward?.rewardItem ||
                                  merchantReward?.reward_type?.replaceAll("_", " ") ||
                                  merchantReward?.name ||
                                  merchantReward?.title ||
                                  merchantReward?.reward_name ||
                                  "Reward"}
                            </div>

                          </div>
                      ) : null}

                      {!passedFirst ? (
                          <div style={styles.bigLine}
                          >
                            {text.rewardWaitingVisitSubtitle}
                          </div>
                      ) : null}

                      <div style={styles.storeBadges}>

                        <a
                            href="https://play.google.com/store/apps/details?id=com.klerk.mobileapp"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <img
                              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                              alt="Google Play"
                              style={styles.badge}
                          />
                        </a>

                        <a
                            href="https://apps.apple.com/in/app/klerk/id6760263246"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <img
                              src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                              alt="App Store"
                              style={styles.badge}
                          />
                        </a>

                      </div>

                    </>
                ) : (

                    <>

                      <div style={styles.storeBadges}>
                        <a
                            href="https://play.google.com/store/apps/details?id=com.klerk.mobileapp"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <img
                              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                              alt="Google Play"
                              style={styles.badge}
                          />
                        </a>

                        <a
                            href="https://apps.apple.com/in/app/klerk/id6760263246"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          <img
                              src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                              alt="App Store"
                              style={styles.badge}
                          />
                        </a>
                      </div>

                    </>

                )}
              </>
          ) : (
              <>
                <div style={{ ...styles.bigLine, marginTop: 18, fontWeight: 600, color: "#64748b" }}>
                  {result?.message}
                </div>
                <div style={{ ...styles.smallLine, marginTop: 12 }}>
                  {isFr
                      ? "Touchez le tag de nouveau ou actualisez la page pour réessayer."
                      : "Tap the tag again or refresh the page to retry."}
                </div>
              </>
          )}
        </>
    );
  };

  return renderPageShell(
      <>
        {noTapToken ? (
            <div style={{ textAlign: "center", padding: 32 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📱</div>

              <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: 8,
                  }}
              >
                {text.noTapTitle}
              </div>

              <div style={{ fontSize: 14, color: "#64748b" }}>
                {text.noTapSubtitle}
              </div>
            </div>
        ) : claimed ? renderSuccessScreen() : null}
      </>
  );

}